import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { content, type Language } from '../config/siteContent'
import { readConsentChoice, saveConsentChoice, type ConsentChoice } from '../config/consent'
import { routeFor } from '../config/routes'
import { CloseIcon } from './icons'

type CookieConsentProps = {
  language: Language
  open: boolean
  onDismiss: () => void
}

export default function CookieConsent({ language, open, onDismiss }: CookieConsentProps) {
  const panelRef = useRef<HTMLElement>(null)
  const [currentChoice, setCurrentChoice] = useState<ConsentChoice | null>(null)
  const copy = content[language].cookieConsent
  const titleId = `cookie-consent-title-${language}`

  useEffect(() => {
    if (!open) return

    const storedChoice = readConsentChoice()
    setCurrentChoice(storedChoice)

    if (storedChoice) {
      const frame = window.requestAnimationFrame(() => panelRef.current?.focus({ preventScroll: true }))
      return () => window.cancelAnimationFrame(frame)
    }
  }, [language, open])

  if (!open) return null

  const choose = (choice: ConsentChoice) => {
    saveConsentChoice(choice)
    setCurrentChoice(choice)
    onDismiss()
  }

  return (
    <aside
      ref={panelRef}
      className="cookie-consent"
      role="dialog"
      aria-labelledby={titleId}
      aria-describedby={`${titleId}-description`}
      aria-live="polite"
      tabIndex={-1}
    >
      {currentChoice && (
        <button className="cookie-consent__close" type="button" onClick={onDismiss} aria-label={copy.close}>
          <CloseIcon />
        </button>
      )}
      <div className="cookie-consent__copy">
        <p className="cookie-consent__eyebrow">{copy.eyebrow}</p>
        <h2 id={titleId}>{copy.title}</h2>
        <p id={`${titleId}-description`}>{copy.text}</p>
        <Link className="cookie-consent__policy-link" to={`${routeFor(language, 'privacy')}#cookies`}>{copy.policyLink}</Link>
        {currentChoice && (
          <p className="cookie-consent__status" role="status">
            {currentChoice === 'accepted' ? copy.acceptedStatus : copy.rejectedStatus}
          </p>
        )}
      </div>
      <div className="cookie-consent__actions">
        <button
          className={`cookie-consent__button cookie-consent__button--reject${currentChoice === 'rejected' ? ' is-current' : ''}`}
          type="button"
          onClick={() => choose('rejected')}
          aria-pressed={currentChoice === 'rejected'}
        >
          {copy.reject}
        </button>
        <button
          className={`cookie-consent__button cookie-consent__button--accept${currentChoice === 'accepted' ? ' is-current' : ''}`}
          type="button"
          onClick={() => choose('accepted')}
          aria-pressed={currentChoice === 'accepted'}
        >
          {copy.accept}
        </button>
      </div>
    </aside>
  )
}
